import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Paper,
  Alert,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Message as MessageIcon,
  Palette as PaletteIcon,
  DynamicForm as FormIcon,
  Security as PrivacyIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Styled Components - Matching TextColors.js/DisplayOptions.js pattern
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SettingsToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
}));

const ColorPickerRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ColorSwatch = styled(Box)(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: color,
  border: "2px solid rgba(0,0,0,0.1)",
  flexShrink: 0,
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

// Form Field Cards
const FormFieldCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 12,
  marginBottom: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.primary.light,
  },
}));

const AddFieldButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5),
  borderStyle: "dashed",
  borderWidth: 2,
}));

const QuillContainer = styled(Box)(({ theme }) => ({
  "& .ql-container": {
    borderRadius: "0 0 8px 8px",
    minHeight: 100,
    fontSize: 14,
  },
  "& .ql-toolbar": {
    borderRadius: "8px 8px 0 0",
    backgroundColor: theme.palette.grey[50],
  },
  "& .ql-editor": {
    minHeight: 80,
  },
}));

// Preview Components
const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
}));

const ChatWindowPreview = styled(Box)({
  maxWidth: 350,
  backgroundColor: "#ffffff",
  borderRadius: 16,
  boxShadow: "0 5px 40px rgba(0,0,0,0.15)",
  overflow: "hidden",
  margin: "0 auto",
  display: "flex",
  flexDirection: "column",
  height: 520,
});

const ChatHeaderPreview = styled(Box)(({ buttoncolor, fontcolor }) => ({
  backgroundColor: buttoncolor || "#4CAF50",
  color: fontcolor || "#ffffff",
  padding: "16px",
  fontWeight: 600,
  fontSize: "1rem",
  display: "flex",
  alignItems: "center",
  gap: 12,
}));

const ContactFormBodyPreview = styled(Box)(({ bgcolor, textcolor }) => ({
  flex: 1,
  padding: 20,
  backgroundColor: bgcolor || "#f0f4f8",
  color: textcolor || "#000000",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  overflowY: "auto",
}));

const PreviewLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
    }
  }
`;

function ContactForm({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) {
  const [updateWidget] = useMutation(UPDATE_WIDGET);

  // State declarations
  const [unavailableMessage, setUnavailableMessage] = useState(
    "We are sorry, none of our agents are available right now. You are welcome to leave your email and we will contact you as soon as possible."
  );
  const [backgroundColor, setBackgroundColor] = useState("#f0f4f8");
  const [fontColor, setFontColor] = useState("#000000");
  const [enablePrivacy, setEnablePrivacy] = useState(false);
  const [privacyText, setPrivacyText] = useState("Privacy Information Text");
  const [formFields, setFormFields] = useState([
    { name: "Name", field_type: "text", enabled: true, required: true },
    { name: "Email", field_type: "email", enabled: true, required: true },
    { name: "Message", field_type: "textarea", enabled: true, required: true },
  ]);

  // Get widget colors for preview header
  const widgetButtonColor =
    widgetData?.welcome_screen?.button_color || "#4CAF50";
  const widgetFontColor = widgetData?.welcome_screen?.font_color || "#ffffff";
  const businessName =
    widgetData?.business_details?.display_name || "Your Business";

  // Initialize state from widgetData when editing
  useEffect(() => {
    if (widgetData?.contact_form) {
      setUnavailableMessage(
        widgetData.contact_form.unavailable_message ||
          "We are sorry, none of our agents are available right now. You are welcome to leave your email and we will contact you as soon as possible."
      );
      setBackgroundColor(widgetData.contact_form.background_color || "#f0f4f8");
      setFontColor(widgetData.contact_form.text_color || "#000000");
      setEnablePrivacy(widgetData.contact_form.privacy_enabled ?? false);
      setPrivacyText(
        widgetData.contact_form.privacy_text || "Privacy Information Text"
      );
      if (
        widgetData.contact_form.form_fields &&
        widgetData.contact_form.form_fields.length > 0
      ) {
        setFormFields(
          widgetData.contact_form.form_fields.map(
            ({ __typename, ...field }) => ({
              name: field.name,
              field_type: field.field_type || "text",
              enabled: field.enabled ?? true,
              required: field.required ?? false,
            })
          )
        );
      }
    }
  }, [widgetData]);

  const handleSaveContactForm = useCallback(async () => {
    if (!widgetId) {
      console.error("No widgetId available");
      return;
    }

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: widgetData?.website_integration
          ? {
              mode: widgetData.website_integration.mode,
              button_style: widgetData.website_integration.button_style,
              position: widgetData.website_integration.position,
              square_buttom: widgetData.website_integration.square_buttom,
              minimize_small_button:
                widgetData.website_integration.minimize_small_button,
              preview: widgetData.website_integration.preview,
              media: widgetData.website_integration.media,
              picture: widgetData.website_integration.picture,
              speeach_bubble_test:
                widgetData.website_integration.speeach_bubble_test,
              call_to_action_button_text:
                widgetData.website_integration.call_to_action_button_text,
              slogan: widgetData.website_integration.slogan,
            }
          : undefined,
        welcome_screen: widgetData?.welcome_screen
          ? {
              font_shadow: widgetData.welcome_screen.font_shadow,
              desc: widgetData.welcome_screen.desc,
              customizable_button:
                widgetData.welcome_screen.customizable_button,
              button_color: widgetData.welcome_screen.button_color,
              font_color: widgetData.welcome_screen.font_color,
              icon_color: widgetData.welcome_screen.icon_color,
              welcome_message: widgetData.welcome_screen.welcome_message,
            }
          : undefined,
        // Preserve contact_options and request_routing
        contact_options: widgetData?.contact_options
          ? {
              chat_enabled: widgetData.contact_options.chat_enabled,
              phone_enabled: widgetData.contact_options.phone_enabled,
              video_enabled: widgetData.contact_options.video_enabled,
              audio_enabled: widgetData.contact_options.audio_enabled,
            }
          : undefined,
        request_routing: widgetData?.request_routing
          ? {
              preselection_enabled:
                widgetData.request_routing.preselection_enabled,
              automatic_response_enabled:
                widgetData.request_routing.automatic_response_enabled,
              automatic_response_text:
                widgetData.request_routing.automatic_response_text,
              lead_form_enabled: widgetData.request_routing.lead_form_enabled,
              privacy_enabled: widgetData.request_routing.privacy_enabled,
              privacy_text: widgetData.request_routing.privacy_text,
            }
          : undefined,
        // Preserve session_end
        session_end: widgetData?.session_end
          ? {
              title: widgetData.session_end.title,
              closing_message: widgetData.session_end.closing_message,
              background_color: widgetData.session_end.background_color,
              text_color: widgetData.session_end.text_color,
              show_feedback_form: widgetData.session_end.show_feedback_form,
              rating_type: widgetData.session_end.rating_type,
            }
          : undefined,
        // Save contact form settings (Step 5)
        contact_form: {
          unavailable_message: unavailableMessage,
          background_color: backgroundColor,
          text_color: fontColor,
          form_fields: formFields.map((field) => ({
            name: field.name,
            field_type: field.field_type || "text",
            enabled: field.enabled,
            required: field.required,
          })),
          privacy_enabled: enablePrivacy,
          privacy_text: privacyText,
        },
        // Preserve visibility_settings
        visibility_settings: widgetData?.visibility_settings
          ? {
              always_on: widgetData.visibility_settings.always_on,
              custom_schedule_enabled:
                widgetData.visibility_settings.custom_schedule_enabled,
              timezone: widgetData.visibility_settings.timezone,
              schedule: widgetData.visibility_settings.schedule,
              hide_on_mobile: widgetData.visibility_settings.hide_on_mobile,
              hide_when_offline:
                widgetData.visibility_settings.hide_when_offline,
              dialog_delay_seconds:
                widgetData.visibility_settings.dialog_delay_seconds,
            }
          : undefined,
      };

      const { data } = await updateWidget({
        variables: { id: widgetId, input: widgetInput },
      });

      console.log("Contact form saved successfully");
      return { widgetId, widgetData: data.updateWidget };
    } catch (err) {
      console.error("Error saving contact form:", err);
      throw err;
    }
  }, [
    widgetId,
    widgetData,
    unavailableMessage,
    backgroundColor,
    fontColor,
    formFields,
    enablePrivacy,
    privacyText,
    updateWidget,
  ]);

  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveContactForm);
    }
  }, [onSave, handleSaveContactForm]);

  const handleFormFieldChange = (index, field, value) => {
    const updatedFields = [...formFields];
    updatedFields[index][field] = value;
    setFormFields(updatedFields);
  };

  const addFormField = () => {
    setFormFields([
      ...formFields,
      { name: "New Field", field_type: "text", enabled: true, required: false },
    ]);
  };

  const removeFormField = (index) => {
    const updatedFields = formFields.filter((_, i) => i !== index);
    setFormFields(updatedFields);
  };

  return (
    <Grid container spacing={4}>
      {/* Header Section */}
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Configure Contact Form
        </Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Customize the contact form that appears when agents are unavailable.
          Visitors can leave their information and you can follow up with them
          later.
        </Alert>
      </Grid>

      {/* Configuration Panel */}
      <Grid item xs={12} md={5}>
        <ConfigPanel elevation={3}>
          {/* Section 1: Unavailable Message */}
          <Section>
            <SectionTitle variant="h6">
              <MessageIcon color="primary" />
              Unavailable Message
            </SectionTitle>
            <StyledTextField
              fullWidth
              multiline
              rows={3}
              value={unavailableMessage}
              onChange={(e) => setUnavailableMessage(e.target.value)}
              placeholder="Enter the message shown when agents are unavailable..."
              size="small"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              This message is displayed when no agents are available to chat
            </Typography>
          </Section>

          {/* Section 2: Colors */}
          <Section>
            <SectionTitle variant="h6">
              <PaletteIcon color="primary" />
              Form Colors
            </SectionTitle>

            <ColorPickerRow>
              <ColorSwatch color={backgroundColor} />
              <MuiColorInput
                format="hex"
                value={backgroundColor}
                onChange={setBackgroundColor}
                label="Background Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block", ml: 7 }}
            >
              Background color of the contact form area
            </Typography>

            <ColorPickerRow>
              <ColorSwatch color={fontColor} />
              <MuiColorInput
                format="hex"
                value={fontColor}
                onChange={setFontColor}
                label="Text Color"
                size="small"
                sx={{ flex: 1 }}
              />
            </ColorPickerRow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", ml: 7 }}
            >
              Color of the text and labels in the form
            </Typography>
          </Section>

          {/* Section 3: Form Fields */}
          <Section>
            <SectionTitle variant="h6">
              <FormIcon color="primary" />
              Form Fields
            </SectionTitle>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Configure which fields are shown and required in the contact form
            </Typography>

            {formFields.map((field, index) => (
              <FormFieldCard key={index}>
                <Box sx={{ flex: 1 }}>
                  <StyledTextField
                    value={field.name}
                    onChange={(e) =>
                      handleFormFieldChange(index, "name", e.target.value)
                    }
                    size="small"
                    fullWidth
                    placeholder="Field name"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.enabled}
                      onChange={(e) =>
                        handleFormFieldChange(
                          index,
                          "enabled",
                          e.target.checked
                        )
                      }
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      Show
                    </Typography>
                  }
                  labelPlacement="top"
                  sx={{ m: 0, minWidth: 50 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.required}
                      onChange={(e) =>
                        handleFormFieldChange(
                          index,
                          "required",
                          e.target.checked
                        )
                      }
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      Required
                    </Typography>
                  }
                  labelPlacement="top"
                  sx={{ m: 0, minWidth: 60 }}
                />
                <Tooltip title="Remove field">
                  <IconButton
                    onClick={() => removeFormField(index)}
                    size="small"
                    color="error"
                    sx={{
                      "&:hover": {
                        backgroundColor: "error.light",
                        color: "white",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </FormFieldCard>
            ))}

            <AddFieldButton
              startIcon={<AddIcon />}
              onClick={addFormField}
              variant="outlined"
              fullWidth
              color="primary"
            >
              Add New Field
            </AddFieldButton>
          </Section>

          {/* Section 4: Privacy */}
          <Section>
            <SectionTitle variant="h6">
              <PrivacyIcon color="primary" />
              Privacy Information
            </SectionTitle>

            <SettingsToggle>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Show Privacy Text
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Display privacy information below the form fields
                </Typography>
              </Box>
              <Switch
                checked={enablePrivacy}
                onChange={(e) => setEnablePrivacy(e.target.checked)}
                color="primary"
              />
            </SettingsToggle>

            {enablePrivacy && (
              <Box
                sx={{
                  mt: 2,
                  pl: 2,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <QuillContainer>
                  <ReactQuill
                    theme="snow"
                    value={privacyText}
                    onChange={setPrivacyText}
                    placeholder="Enter privacy information..."
                  />
                </QuillContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Use formatting to add links, bold text, etc.
                </Typography>
              </Box>
            )}
          </Section>
        </ConfigPanel>
      </Grid>

      {/* Preview Panel */}
      <Grid item xs={12} md={7}>
        <Box
          sx={{
            position: "sticky",
            top: 20,
          }}
        >
          <PreviewContainer elevation={3}>
            <SectionTitle variant="h6" sx={{ mb: 2 }}>
              <ChatIcon color="primary" />
              Live Preview
            </SectionTitle>
            <PreviewLabel>Contact Form View</PreviewLabel>

            <ChatWindowPreview>
              {/* Chat Header - Uses widget theme colors */}
              <ChatHeaderPreview
                buttoncolor={widgetButtonColor}
                fontcolor={widgetFontColor}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ChatIcon sx={{ color: widgetFontColor, fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: widgetFontColor, fontSize: 14 }}
                  >
                    {businessName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: widgetFontColor, opacity: 0.85 }}
                  >
                    Contact Form
                  </Typography>
                </Box>
                <CloseIcon sx={{ color: widgetFontColor, opacity: 0.7 }} />
              </ChatHeaderPreview>

              {/* Contact Form Body - Uses local colors */}
              <ContactFormBodyPreview
                bgcolor={backgroundColor}
                textcolor={fontColor}
              >
                {/* Unavailable Message */}
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.9)",
                    borderRadius: 2,
                    p: 2,
                    mb: 1,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: fontColor, lineHeight: 1.5, fontSize: 13 }}
                  >
                    {unavailableMessage}
                  </Typography>
                </Box>

                {/* Form Fields Preview */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {formFields.map(
                    (field, index) =>
                      field.enabled && (
                        <TextField
                          key={index}
                          label={`${field.name}${field.required ? " *" : ""}`}
                          variant="outlined"
                          fullWidth
                          size="small"
                          disabled
                          multiline={field.field_type === "textarea"}
                          rows={field.field_type === "textarea" ? 2 : 1}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                            },
                            "& .MuiInputLabel-root": {
                              color: fontColor,
                              opacity: 0.7,
                            },
                          }}
                        />
                      )
                  )}
                </Box>

                {/* Privacy Text Preview */}
                {enablePrivacy && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.7)",
                      borderRadius: 1,
                      p: 1.5,
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: fontColor,
                        opacity: 0.8,
                        "& p": { margin: 0 },
                        "& a": { color: widgetButtonColor },
                        fontSize: 11,
                        lineHeight: 1.4,
                      }}
                      dangerouslySetInnerHTML={{ __html: privacyText }}
                    />
                  </Box>
                )}

                {/* Submit Button - Uses widget button color */}
                <Button
                  variant="contained"
                  fullWidth
                  disabled
                  sx={{
                    mt: "auto",
                    backgroundColor: widgetButtonColor,
                    color: widgetFontColor,
                    borderRadius: 2,
                    py: 1.2,
                    fontWeight: 600,
                    "&.Mui-disabled": {
                      backgroundColor: widgetButtonColor,
                      color: widgetFontColor,
                      opacity: 0.9,
                    },
                  }}
                >
                  Submit
                </Button>
              </ContactFormBodyPreview>
            </ChatWindowPreview>

            {/* Preview Info */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "rgba(255,255,255,0.8)",
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <strong>Header colors</strong> are inherited from your widget
                theme (Text & Colors step).
                <br />
                <strong>Form colors</strong> are configured above and apply to
                the form body area.
              </Typography>
            </Box>
          </PreviewContainer>
        </Box>
      </Grid>
    </Grid>
  );
}

export default ContactForm;
